import type {
  WorkbookDiagnostic,
  WorkbookFreeformSheet,
} from "@erp/shared-types";
import type { Schema } from "../planTypes";
import { RESERVED_PREFIX, isReserved } from "../reservedNames";
import { resolveTable, type WorkbookTableItem } from "../tableReferences";
import { rangeHolds, type CellRange } from "./address";
import { freeformReferenceRange, freeformReferences } from "./formula";
import { filledCells, freeformTableLayout } from "./table";

function cellDiagnostic(
  sheet: WorkbookFreeformSheet,
  cell: string,
  code: WorkbookDiagnostic["code"],
  message: string,
): WorkbookDiagnostic {
  return { code, message, location: { part: "cell", itemId: sheet.id, cell } };
}

/** The ids of the other tables a freeform sheet's Formulas read, in reading order. */
export function freeformReads(
  sheet: WorkbookFreeformSheet,
  tables: readonly WorkbookTableItem[],
): string[] {
  const ids = new Set<string>();
  for (const { input } of filledCells(sheet))
    for (const { table } of freeformReferences(input)) {
      if (table.kind === "local") continue;
      const resolved = resolveTable(tables, table);
      if (resolved.ok && resolved.item.id !== sheet.id)
        ids.add(resolved.item.id);
    }
  return [...ids];
}

/** A reference to a table the Workbook doesn't have, by name or by a removed id. */
export function unknownTables(
  sheet: WorkbookFreeformSheet,
  tables: readonly WorkbookTableItem[],
): WorkbookDiagnostic[] {
  return filledCells(sheet).flatMap(({ address, input }) =>
    freeformReferences(input).flatMap(({ table }) => {
      if (table.kind === "local") return [];
      const resolved = resolveTable(tables, table);
      if (resolved.ok) return [];
      const message =
        resolved.reason === "missing"
          ? "This formula refers to a sheet that was removed."
          : resolved.reason === "ambiguous"
            ? `More than one sheet is named '${table.kind === "name" ? table.name : ""}'.`
            : `No sheet is named '${table.kind === "name" ? table.name : ""}'.`;
      return [cellDiagnostic(sheet, address, "unknown-table", message)];
    }),
  );
}

/**
 * A reference by column name to a column its table no longer has (renamed
 * or removed), when the table's columns are known: it gives #REF!.
 */
function missingColumns(
  sheet: WorkbookFreeformSheet,
  tables: readonly WorkbookTableItem[],
  schemas: ReadonlyMap<string, Schema>,
): WorkbookDiagnostic[] {
  return filledCells(sheet).flatMap(({ address, input }) =>
    freeformReferences(input).flatMap((reference) => {
      if (reference.table.kind === "local") return [];
      const resolved = resolveTable(tables, reference.table);
      const schema = resolved.ok ? schemas.get(resolved.item.id) : undefined;
      if (!resolved.ok || !schema) return [];
      const range = freeformReferenceRange(
        reference,
        schema.map((column) => column.name),
      );
      if (range.ok || range.missing === null) return [];
      return [
        cellDiagnostic(
          sheet,
          address,
          "freeform-missing-column",
          `This formula refers to the column '${range.missing}' of '${resolved.item.name}', which it no longer has (renamed or removed), so it gives #REF!.`,
        ),
      ];
    }),
  );
}

/**
 * The most work the static circular-reference check does (range members
 * found plus edges followed). A sheet past it, such as thousands of formulas
 * each summing a range over all of them, is left to the spreadsheet engine,
 * which reports the cycle at Run as a circular reference.
 */
const CYCLE_CHECK_BUDGET = 2_000_000;

type Cell = ReturnType<typeof filledCells>[number];

/** The rectangles a Formula's references to this sheet's own grid cover. */
function localRanges(input: string): CellRange[] {
  return freeformReferences(input).flatMap((reference) => {
    const resolved =
      reference.table.kind === "local" && freeformReferenceRange(reference);
    return resolved && resolved.ok ? [resolved.range] : [];
  });
}

/** Each formula cell's local reads, as the formula cells they cover; null past the budget. */
function formulaReads(formulas: readonly Cell[]): number[][] | null {
  let budget = CYCLE_CHECK_BUDGET;
  // One member list per distinct range, however many formulas name it.
  const members = new Map<string, number[]>();
  const reads: number[][] = [];
  for (const { input } of formulas) {
    const targets = new Set<number>();
    for (const range of localRanges(input)) {
      const key = `${range.start.column}:${range.start.row}:${range.end.column}:${range.end.row}`;
      let covered = members.get(key);
      if (!covered) {
        budget -= formulas.length;
        covered = formulas.flatMap((cell, index) =>
          rangeHolds(range, cell.position) ? [index] : [],
        );
        members.set(key, covered);
      }
      budget -= covered.length;
      if (budget < 0) return null;
      for (const index of covered) targets.add(index);
    }
    reads.push([...targets]);
  }
  return reads;
}

/**
 * The nodes on a cycle of `next`: each strongly connected component of more
 * than one node, or a node reading itself. Tarjan's algorithm, iteratively,
 * so a long chain of cells can't overflow the stack.
 */
class CycleFinder {
  private readonly order: number[];
  private readonly low: number[];
  private readonly onStack: boolean[];
  private readonly stack: number[] = [];
  private readonly frames: { node: number; edge: number }[] = [];
  private counter = 0;
  readonly cyclic = new Set<number>();

  constructor(
    count: number,
    private readonly next: (node: number) => readonly number[],
  ) {
    this.order = new Array<number>(count).fill(-1);
    this.low = new Array<number>(count).fill(0);
    this.onStack = new Array<boolean>(count).fill(false);
    for (let root = 0; root < count; root += 1)
      if (this.order[root] === -1) this.search(root);
  }

  private search(root: number): void {
    this.open(root);
    for (let frame = this.frames.at(-1); frame; frame = this.frames.at(-1)) {
      const target = this.next(frame.node)[frame.edge];
      frame.edge += 1;
      if (target === undefined) this.close(frame.node);
      else this.follow(frame.node, target);
    }
  }

  private open(node: number): void {
    this.order[node] = this.counter;
    this.low[node] = this.counter;
    this.counter += 1;
    this.stack.push(node);
    this.onStack[node] = true;
    this.frames.push({ node, edge: 0 });
  }

  private follow(node: number, target: number): void {
    if (target === node) this.cyclic.add(node);
    if (this.order[target] === -1) this.open(target);
    else if (this.onStack[target]) this.lower(node, this.order[target]);
  }

  private lower(node: number, value: number | undefined): void {
    this.low[node] = Math.min(this.low[node] ?? 0, value ?? 0);
  }

  private close(node: number): void {
    this.frames.pop();
    const parent = this.frames.at(-1);
    if (parent) this.lower(parent.node, this.low[node]);
    if (this.low[node] === this.order[node]) this.popComponent(node);
  }

  private popComponent(root: number): void {
    const component: number[] = [];
    for (let member = this.stack.pop(); member !== undefined;) {
      this.onStack[member] = false;
      component.push(member);
      if (member === root) break;
      member = this.stack.pop();
    }
    if (component.length > 1)
      for (const member of component) this.cyclic.add(member);
  }
}

/** Formula cells that read themselves through other cells (a circular reference). */
function cycleDiagnostics(sheet: WorkbookFreeformSheet): WorkbookDiagnostic[] {
  const formulas = filledCells(sheet).filter(({ input }) =>
    input.startsWith("="),
  );
  const reads = formulaReads(formulas);
  if (!reads) return [];
  const { cyclic } = new CycleFinder(
    formulas.length,
    (node) => reads[node] ?? [],
  );
  return formulas
    .filter((_, index) => cyclic.has(index))
    .map(({ address }) =>
      cellDiagnostic(
        sheet,
        address,
        "cell-cycle",
        "This formula refers to itself through other cells (a circular reference).",
      ),
    );
}

/** Problems with the declared table: no range yet, or a header cell that can't name a column. */
function tableDiagnostics(sheet: WorkbookFreeformSheet): WorkbookDiagnostic[] {
  const layout = freeformTableLayout(sheet);
  if (!layout)
    return [
      {
        code: "freeform-table-range",
        message:
          "Choose the cells its result table reads: a range whose first row names the columns.",
        location: { part: "item", itemId: sheet.id },
      },
    ];
  const seen = new Set<string>();
  return layout.columns.flatMap(({ name, header }) => {
    const input = sheet.cells[header] ?? "";
    const problem = !name
      ? "Type a column name in this header cell."
      : input.startsWith("=")
        ? "A header cell names its column, so it can't be a formula."
        : seen.has(name.toLowerCase())
          ? "Column names must be unique, ignoring case."
          : isReserved(name)
            ? `Column names beginning with ${RESERVED_PREFIX} are reserved.`
            : null;
    seen.add(name.toLowerCase());
    return problem
      ? [cellDiagnostic(sheet, header, "freeform-table-header", problem)]
      : [];
  });
}

/**
 * A freeform sheet's Diagnostics: its table, its references (to tables and
 * to their columns, by `schemas`) and its circular references.
 */
export function planFreeform(
  sheet: WorkbookFreeformSheet,
  tables: readonly WorkbookTableItem[],
  schemas: ReadonlyMap<string, Schema> = new Map(),
): WorkbookDiagnostic[] {
  return [
    ...tableDiagnostics(sheet),
    ...unknownTables(sheet, tables),
    ...missingColumns(sheet, tables, schemas),
    ...cycleDiagnostics(sheet),
  ];
}
